/**
 * Restore — Hard-delete now modal — per docs/specs/restore.md §6.
 *
 * Owner-only destructive action bypassing the 30d retention window. Requires
 * reason + 2FA code; writes an immutable `hard_delete_log` row.
 *
 * @copyright 2025 Glimbr Pty Ltd
 * @version 2026-04-21-v3
 */
import { useEffect, useState } from 'react';
import { AlertTriangle } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { Modal } from '../components/modal';
import { RoleButton } from '../components/role-gate';
import { Chip } from '../components/chip';
import { useToast } from '../components/toast';
import { HttpError, apiPost, getHttpErrorMessage } from '../../lib/http';
import { useAdminSession } from '../../lib/auth';
import { appendAuditEntry } from '../../lib/mutations';
import type { GdprExportRow } from '../../lib/types';

export function RestoreHardDeleteModal({
    row,
    onClose
}: {
    row: GdprExportRow | null;
    onClose: () => void;
}) {
    const [reason, setReason] = useState('');
    const [code, setCode] = useState('');
    const [confirmPhrase, setConfirmPhrase] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const qc = useQueryClient();
    const { toast } = useToast();
    const session = useAdminSession();
    const canSubmit =
        !!row &&
        reason.trim().length > 0 &&
        /^\d{6}$/.test(code) &&
        confirmPhrase.trim().toUpperCase() === 'PURGE';

    useEffect(() => {
        if (!row) {
            setReason('');
            setCode('');
            setConfirmPhrase('');
            setSubmitting(false);
            return;
        }
    }, [row]);

    const handleClose = () => {
        setReason('');
        setCode('');
        setConfirmPhrase('');
        onClose();
    };

    if (!row) return null;

    const codeValid = /^\d{6}$/.test(code);

    const recordDryRunAudit = (payload: { reason: string }) => {
        appendAuditEntry(qc, {
            actor: session.email,
            actor_role: session.role,
            action: 'restore.hard_delete',
            target_type: 'gdpr_export',
            target_id: row.user_email,
            severity: 'destructive',
            reason: `[DRY-RUN] ${payload.reason}`
        });
    };

    const isNotIntegrated = (error: unknown) =>
        error instanceof HttpError && [404, 405, 501].includes(error.status);

    const refreshRestoreRows = async () => {
        await Promise.all([
            qc.invalidateQueries({ queryKey: ['admin', 'restore', 'exports'] }),
            qc.invalidateQueries({ queryKey: ['admin', 'restore', 'kpis'] })
        ]);
    };

    const handleConfirm = async () => {
        if (!row || !canSubmit || submitting) return;
        setSubmitting(true);

        try {
            await apiPost(`/api/admin/restore/exports/${row.id}/hard-delete`, {
                reason: reason.trim(),
                confirm_phrase: 'PURGE',
                code
            });
            await refreshRestoreRows();
            appendAuditEntry(qc, {
                actor: session.email,
                actor_role: session.role,
                action: 'restore.hard_delete',
                target_type: 'gdpr_export',
                target_id: row.user_email,
                severity: 'destructive',
                reason: reason.trim()
            });
            toast('Hard-delete queued', {
                kind: 'danger',
                detail: row.user_email
            });
            handleClose();
        } catch (error) {
            if (isNotIntegrated(error)) {
                recordDryRunAudit({ reason: reason.trim() });
                toast('Dry-run recorded', {
                    kind: 'info',
                    detail: `Action not executed against worker yet for ${row.user_email}`
                });
                handleClose();
                return;
            }

            toast('Hard-delete failed', {
                kind: 'danger',
                detail: getHttpErrorMessage(error, 'Could not schedule hard-delete now')
            });
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Modal
            open={!!row}
            onClose={handleClose}
            title="Hard-delete now"
            subtitle={row.user_email}
            width={560}
            footer={
                <>
                    <button className="btn btn-ghost" onClick={handleClose} disabled={submitting}>
                        Cancel
                    </button>
                    <RoleButton
                        scope="owner_only"
                        className="btn btn-primary"
                        onClick={handleConfirm}
                        disabled={!canSubmit || submitting}
                    >
                        {submitting ? 'Purging…' : 'Purge immediately'}
                    </RoleButton>
                </>
            }
        >
            <div
                style={{
                    display: 'grid',
                    gridTemplateColumns: '18px 1fr',
                    gap: 10,
                    padding: 12,
                    background: 'var(--danger-surface)',
                    border: '1px solid color-mix(in oklab, var(--danger) 30%, transparent)',
                    borderRadius: 8,
                    marginBottom: 14,
                    color: 'var(--text-primary)',
                    fontSize: 12
                }}
            >
                <AlertTriangle size={16} style={{ color: 'var(--danger)', marginTop: 1 }} />
                <div>
                    <strong>Bypasses the 30-day retention window.</strong>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
                        An immutable <span className="mono">hard_delete_log</span> record will be
                        written with every purged row id, your admin id, the reason below, and a
                        timestamp. R2 objects are purged synchronously — this cannot be undone.
                    </div>
                </div>
            </div>

            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 14 }}>
                <Chip tone="danger">{row.type.replace('_', ' ')}</Chip>
                <Chip tone="muted">requested {row.requested_at}</Chip>
                {row.stuck_days ? <Chip tone="warning">stuck {row.stuck_days}d</Chip> : null}
            </div>

            <div className="form-field">
                <label className="form-label" htmlFor="hd-reason">
                    Reason (required)
                </label>
                <textarea
                    id="hd-reason"
                    className="form-textarea"
                    value={reason}
                    onChange={(e) => setReason(e.target.value)}
                    placeholder="e.g. user fulfilled GDPR Art. 17 request; ticket #PD-2521"
                    style={{ minHeight: 80 }}
                />
                <div className="form-hint">
                    This string is written verbatim to <span className="mono">hard_delete_log</span>
                    .
                </div>
            </div>

            <div className="form-field">
                <label className="form-label" htmlFor="hd-confirm">
                    Type PURGE to confirm
                </label>
                <input
                    id="hd-confirm"
                    className="form-input mono"
                    value={confirmPhrase}
                    onChange={(e) => setConfirmPhrase(e.target.value)}
                    placeholder="PURGE"
                />
                <div className="form-hint">
                    Extra confirmation keeps hard-delete from being triggered by a single click.
                </div>
            </div>

            <div className="form-field">
                <label className="form-label" htmlFor="hd-2fa">
                    2FA code
                </label>
                <input
                    id="hd-2fa"
                    className="form-input mono"
                    inputMode="numeric"
                    maxLength={6}
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    placeholder="000000"
                    style={{ letterSpacing: 4 }}
                />
                <div className="form-hint">
                    6-digit TOTP from your authenticator app.{' '}
                    {codeValid ? '' : 'Required to submit.'}
                </div>
            </div>
        </Modal>
    );
}
